import React, { useState } from 'react';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import TextInput from '@/components/Forms/TextInput';
import useRoutes from '@/hooks/useRoutes';
import { useForm } from '@inertiajs/react';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PasswordChange: React.FC = () => {
    const { t } = useTranslation();
    const { route } = useRoutes();
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const {
        data,
        setData,
        post,
        processing,
        errors,
        clearErrors,
        reset,
    } = useForm({
        current_password: '',
        new_password: '',
        password_confirmation: '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        post(route('my-account').toString(), {
            onSuccess: () => {
                setIsChangingPassword(false);
                reset();
            },
        });
    };

    const handleCancel = () => {
        reset();
        clearErrors();
        setIsChangingPassword(false);
    };

    return (
        <div className="border-t border-white/40 px-4 py-6 dark:border-white/10 sm:px-8">
            <div className="mb-6 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {t('myAccount.changePassword')}
                </h3>
                {!isChangingPassword && (
                    <SecondaryButton
                        type="button"
                        onClick={() => setIsChangingPassword(true)}
                        size="md"
                    >
                        <PencilIcon className="h-4 w-4" />
                        {t('myAccount.changePassword')}
                    </SecondaryButton>
                )}
            </div>

            {isChangingPassword ? (
                <form onSubmit={handleSubmit} translate="no">
                    <div className="space-y-4">
                        <TextInput
                            label={t('myAccount.currentPassword')}
                            name="current_password"
                            type="password"
                            value={data.current_password}
                            onChange={(value) =>
                                setData('current_password', value)
                            }
                            placeholder={t('myAccount.currentPassword')}
                            error={errors.current_password}
                        />

                        <TextInput
                            label={t('myAccount.newPassword')}
                            name="new_password"
                            type="password"
                            value={data.new_password}
                            onChange={(value) =>
                                setData('new_password', value)
                            }
                            placeholder={t('myAccount.newPassword')}
                            error={errors.new_password}
                        />

                        <TextInput
                            label={t('myAccount.confirmNewPassword')}
                            name="password_confirmation"
                            type="password"
                            value={data.password_confirmation}
                            onChange={(value) =>
                                setData('password_confirmation', value)
                            }
                            placeholder={t('myAccount.confirmNewPassword')}
                            error={errors.password_confirmation}
                        />

                        <div className="flex gap-3">
                            <PrimaryButton
                                type="submit"
                                loading={processing}
                                size="md"
                            >
                                {t('myAccount.updatePassword')}
                            </PrimaryButton>
                            <SecondaryButton
                                type="button"
                                onClick={handleCancel}
                                disabled={processing}
                                size="md"
                            >
                                {t('common.cancel')}
                            </SecondaryButton>
                        </div>
                    </div>
                </form>
            ) : (
                <p className="text-sm text-gray-600 dark:text-neutral-400">
                    {t('myAccount.passwordDescription')}
                </p>
            )}
        </div>
    );
};

export default PasswordChange;
